import axios, { CancelTokenSource } from 'axios';
import CheckBox from 'components/final-form/CheckBox';
import Input from 'components/final-form/Input';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import LoaderComponent from 'components/LoaderComponent';
import { FormApi, FormState } from 'final-form';
import moment from 'moment';
import React, { Component } from 'react';
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form'
import ReactHotkeys from 'react-hot-keys';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid } from 'semantic-ui-react';
import { searchOnboardPatients } from 'services/patientServices';
import styled from 'styled-components';
import { debounce } from 'ts-debounce';
import { OnboardPatientSearchRequest, PatientResponse } from 'ts-types/api.types';
import { noop } from 'utils/functionUtils';
import { isKeyCheck } from 'utils/keyUtils';
import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';

interface PatientSearchValues extends FormState {
  values: Partial<OnboardPatientSearchRequest>;
}

const PatientSearchFormGrid = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  margin: 5px;
`;

const FormDiv = styled.div`
  display: flex;
  flex-direction: row;
  flex: 0 0 auto;
`;

const SearchFormLeftDiv = styled.div`
  flex: 1 0 auto;
`;

const TableWrapperDiv = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
  min-height: 200px;

  .patients-search-container {
    margin-top: 2rem;
    flex: 1 1 auto;
  }
`;

const rowStyle = {
  cursor: "pointer",
  userSelect: "none"
};

const selectedRow = {
  ...rowStyle,
  backgroundColor: "#e0e0e0"
};

const CheckboxDiv = styled.div`
  display: flex;
  flex-direction: row;

  .checkbox {
    margin-right: 1.5rem;
  }
`;

const StyledLabel = styled.div`
  margin-bottom: .8rem;
`;

interface Props extends MessageSourceProps, AuthConsumerRenderProps {
  onPatientSelected: (patient?: PatientResponse) => void,
}

interface State {
  foundPatients: PatientResponse[],
  selectedPatientIx?: number,
  selectedPatient?: PatientResponse,
  patientsLoaded: boolean
}

class PatientSearch extends Component<Props, State> {

  private cancelTokenSource?: CancelTokenSource = undefined;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      foundPatients: [],
      selectedPatientIx: undefined,
      selectedPatient: undefined,
      patientsLoaded: true
    };
  }

  componentWillUnmount() {
    this.cancelTokenSource && this.cancelTokenSource.cancel("component_unmount");
  }

  resetSearchData = () => {
    this.setState({
      foundPatients: [],
      selectedPatient: undefined
    });
  };

  onEnterPressed = (e: any) => {

    const {selectedPatientIx} = this.state;

    if (e.key == "Enter") {
      const {foundPatients} = this.state;

      if (foundPatients.length > 0 && selectedPatientIx !== undefined) {
        this.onPatientChosen(foundPatients[selectedPatientIx]);
      }
    }
  };

  onPatientChosen = (selectedPatient: PatientResponse | undefined) => {

    const {onPatientSelected} = this.props;

    onPatientSelected(selectedPatient);
    this.resetSearchData();
  };

  markPatient = (patientIndex: number) => {

    const {foundPatients} = this.state;
    this.setState({
      selectedPatient: foundPatients[patientIndex]
    });
  };

  resetSearchPatients = (form: FormApi) => {
    form.reset();
  };

  searchPatient = (values?: Partial<OnboardPatientSearchRequest>) => {
    const {currentUser} = this.props;

    const mandateId = currentUser!.userConfig.mandateId;

    if (this.cancelTokenSource) {
      this.cancelTokenSource.cancel("stale_search");
    }
    this.cancelTokenSource = axios.CancelToken.source();

    const onFinally = () => {
      this.setState({
        patientsLoaded: true
      })
    };

    this.setState({patientsLoaded: false});

    if (values) {
      const searchRequest: Partial<OnboardPatientSearchRequest> = {
        ...values
      };
      searchRequest.mandateId = mandateId;

      searchOnboardPatients(this.cancelTokenSource, searchRequest).then((patients) => {
            this.setState({
              foundPatients: patients,
              selectedPatientIx: patients.length > 0 ? 0 : undefined,
              selectedPatient: patients.length > 0 ? patients[0] : undefined
            });
          })
          .catch(noop)
          .finally(onFinally);
    }
  };

  handleSearch = debounce(({values}: Partial<PatientSearchValues>) => {

    this.searchPatient(values);

  }, 300);

  onSelectPatientRow(foundPatient: any, index: any) {
    this.setState({
      selectedPatient: foundPatient,
      selectedPatientIx: index
    });
  };

  changeSelectedPatient(event: any, source?: string) {
    event.persist();
    const {selectedPatientIx, foundPatients} = this.state;

    const isKey: any = isKeyCheck(event);

    if (selectedPatientIx !== undefined) {

      const goDown = foundPatients.length > 0 && (selectedPatientIx + 1 <= foundPatients.length - 1);
      const goUp = foundPatients.length > 0 && (selectedPatientIx - 1 >= 0);

      const selectPatient = selectedPatientIx >= 0 && selectedPatientIx <= foundPatients.length - 1;

      if (goDown && isKey.arrowDown) {
        const index = selectedPatientIx + 1;
        this.onSelectPatientRow(foundPatients[index], index);
      } else if (goUp && isKey.arrowUp) {
        const index = selectedPatientIx - 1;
        this.onSelectPatientRow(foundPatients[index], index);
      } else if (selectPatient && isKey.enter) {
        this.onEnterPressed(event);
      }
    }

    const tableContainer = document.getElementById('patient-search-table-container');
    const focusedTable = tableContainer ? tableContainer
        .querySelector('.ReactVirtualized__Grid.ReactVirtualized__Table__Grid') : null;

    if (tableContainer && (isKey.pageUp || isKey.pageDown)) {
      tableContainer.focus();
    }

    const activeElement = document.activeElement === tableContainer;

    if (isKey.arrowUp || isKey.pageUp || isKey.pageDown) {
      event.preventDefault();
    }

    if (isKey.pageUp && focusedTable && activeElement) {
      //@ts-ignore
      const height = focusedTable.offsetHeight;

      if (selectedPatientIx !== undefined) {
        const jumpTo = selectedPatientIx - Math.floor(height / 25);

        if (jumpTo < 0) {
          this.onSelectPatientRow(foundPatients[0], 0);
        } else {
          this.onSelectPatientRow(foundPatients[jumpTo], jumpTo);
        }
      }
    }

    if (isKey.pageDown && focusedTable && activeElement) {
      //@ts-ignore
      const height = focusedTable.offsetHeight;

      if (selectedPatientIx !== undefined) {

        const jumpTo = Math.floor(height / 25) + selectedPatientIx;

        if (jumpTo > foundPatients.length - 1) {
          this.onSelectPatientRow(foundPatients[foundPatients.length - 1], foundPatients.length - 1);
        } else {
          this.onSelectPatientRow(foundPatients[jumpTo], jumpTo);
        }
      }
    }

    if (source) {
      const searchElementId = source === 'search' ? "patient-search-input" : "patient-first-name-search-input";
      const searchInputElement = document.getElementById(searchElementId)
      if (searchInputElement) {
        searchInputElement.focus();
      }
    }

  }

  render() {

    const {selectedPatient, patientsLoaded} = this.state;
    const {getMessage} = this.props;

    return (
        <PatientSearchFormGrid>
          <FinalForm
              onSubmit={() => this.onPatientChosen(selectedPatient)}
              initialValues={{}}
              subscription={{values: true, submitting: true}}
              render={this.renderSearchForm}
          />
          <TableWrapperDiv>
            {patientsLoaded
                ? this.renderPatientsSegment()
                : <LoaderComponent message={getMessage("patient.loading")}/>}
          </TableWrapperDiv>
        </PatientSearchFormGrid>
    );
  }

  renderSearchForm = ({values, form, submitting}: FormRenderProps) => {
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <FormDiv>
            <ReactHotkeys
                keyName="alt+n,option+n"
                filter={() => true}
            >
              <SearchFormLeftDiv>
                <PraxFormGrid>
                  <React.Fragment>
                    <Grid.Row>
                      <Grid.Column width={3}>
                        <StyledLabel>{getMessage("patient.birthDate")}</StyledLabel>
                        <Field
                            name="birthDate"
                            component={Input}
                            fluid
                            autoFocus
                            onKeyDown={(e: any) => this.changeSelectedPatient(e, 'search')}
                        />
                      </Grid.Column>
                      <Grid.Column width={3}>
                        <StyledLabel>
                          {`${getMessage("patient.search.checkbox.id")}, ${getMessage("patient.name")}`}
                        </StyledLabel>
                        <Field
                            name="fullNameAndId"
                            component={Input}
                            fluid
                            onKeyDown={(e: any) => this.changeSelectedPatient(e, 'search')}
                        />
                      </Grid.Column>
                      <Grid.Column width={3}>
                        <StyledLabel>{getMessage("patient.ahvNumber")}</StyledLabel>
                        <Field
                            name="ahvNumber"
                            component={Input}
                            fluid
                            onKeyDown={(e: any) => this.changeSelectedPatient(e, 'search')}
                        />
                      </Grid.Column>
                      <Grid.Column width={3}>
                        <StyledLabel>{getMessage("patient.email")}</StyledLabel>
                        <Field
                            name="email"
                            component={Input}
                            fluid
                            onKeyDown={(e: any) => this.changeSelectedPatient(e, 'search')}
                        />
                      </Grid.Column>
                      <Grid.Column width={3}>
                        <StyledLabel>{getMessage("patient.city")}</StyledLabel>
                        <Field
                            name="city"
                            component={Input}
                            fluid
                            onKeyDown={(e: any) => this.changeSelectedPatient(e, 'search')}
                        />
                      </Grid.Column>
                    </Grid.Row>
                    <Grid.Row>
                      <Grid.Column width={3}>
                        <CheckboxDiv>
                          <Field
                              name="includeInactive"
                              component={CheckBox}
                              label={getMessage('patient.search.checkbox.inactive')}
                          />
                        </CheckboxDiv>
                      </Grid.Column>
                      <Grid.Column width={12}>
                        <Button compact
                                type="button"
                                floated={"right"}
                                size="tiny"
                                primary
                                onClick={() => this.searchPatient(values)}
                                disabled={submitting}
                        >
                          {getMessage("search")}
                        </Button>
                        <Button compact
                                type="button"
                                floated={"right"}
                                style={{marginRight: ".5rem"}}
                                size="tiny"
                                secondary
                                onClick={() => this.resetSearchPatients(form)}
                                disabled={submitting}
                        >
                          {getMessage("button.reset")}
                        </Button>
                      </Grid.Column>
                    </Grid.Row>
                  </React.Fragment>
                </PraxFormGrid>
              </SearchFormLeftDiv>
            </ReactHotkeys>
          </FormDiv>
          <FormSpy subscription={{values: true}} onChange={this.handleSearch}/>
        </React.Fragment>
    );
  };

  rowGetter = ({index}: any) => {
    const {foundPatients} = this.state;

    return foundPatients[index];
  };

  foundPatientRowRenderer = ({className, columns, index, key, style}: any) => {

    const a11yProps = {'aria-rowindex': index + 1};
    const {foundPatients, selectedPatient} = this.state;

    let styles = {
      ...style,
      ...rowStyle
    };

    if (selectedPatient && selectedPatient.id == foundPatients[index].id) {
      styles = {
        ...style,
        ...selectedRow
      }
    }

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={styles}
            onClick={() => this.markPatient(index)}
            onDoubleClick={() => this.onPatientChosen(foundPatients[index])}
        >
          {columns}
        </div>
    );
  };

  fullNameDateCellRenderer = ({rowData}: any) => {
    if (rowData.doctorProblematic) {
      return <span style={{color: "red"}}>{`${rowData.name} ${rowData.firstName}`}</span>
    }
    return `${rowData.name} ${rowData.firstName}`;
  };

  birthDateCellRenderer = ({cellData}: any) => {
    if (moment(cellData).isValid()) {
      return (
          <div>{moment(cellData).format("DD.MM.YYYY")}</div>
      );
    }
    return "";
  };

  addressCellRenderer = ({rowData}: any) => {
    return `${rowData.street}, ${rowData.zip}`;
  };

  renderPatientsSegment = () => {

    const {foundPatients, selectedPatientIx} = this.state;
    const {getMessage} = this.props;

    return (

        <div id="patient-search-table-container"
             className="patients-search-container"
             tabIndex={0}
             onKeyDown={(event) => this.changeSelectedPatient(event)}
        >
          <VirtualizedTable
              rowCount={foundPatients.length}
              rowGetter={({index}: any) => foundPatients[index]}
              rowRenderer={this.foundPatientRowRenderer}
              scrollToIndex={selectedPatientIx ? selectedPatientIx : 0}
              columns={[
                {
                  width: 100,
                  label: getMessage('patient.search.table.id'),
                  dataKey: "id"
                },
                {
                  width: 500,
                  label: getMessage('patient.search.table.nameAndLastName'),
                  dataKey: "name",
                  cellRenderer: this.fullNameDateCellRenderer
                },
                {
                  width: 250,
                  label: getMessage('patient.search.table.birthdate'),
                  dataKey: "birthDate",
                  cellRenderer: this.birthDateCellRenderer
                },
                {
                  width: 400,
                  label: getMessage('patient.search.table.email'),
                  dataKey: "email"
                },
                {
                  width: 600,
                  label: getMessage('patient.search.table.address'),
                  dataKey: "zip",
                  cellRenderer: this.addressCellRenderer
                },
                {
                  width: 350,
                  label: getMessage('patient.search.table.city'),
                  dataKey: "city"
                },
                {
                  width: 350,
                  label: getMessage('patient.search.table.ahvNumber'),
                  dataKey: "ahvNumber"
                },
                {
                  width: 400,
                  label: getMessage('patient.search.table.insurance'),
                  dataKey: "insuranceDescription"
                },
                {
                  width: 400,
                  label: getMessage('patient.search.table.company'),
                  dataKey: "companyDescription"
                }
              ]}
          />
        </div>
    )
  };

}

export default withMessages(withAuthContext(PatientSearch));
